<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useSystemAdminUserStore } from '@/stores/systemAdminUserStore'
import { useToast } from 'primevue/usetoast'
import Dialog from 'primevue/dialog'
import Password from 'primevue/password'
import BaseButton from '@/components/base/BaseButton.vue'

interface Props {
  visible: boolean
  userId?: string // Required for admin mode
  userName?: string // Optional, to show in the header
  isAdminMode?: boolean // true if admin is resetting another user's password
}

const props = withDefaults(defineProps<Props>(), {
  isAdminMode: false
})

const emit = defineEmits(['update:visible', 'success'])

const authStore = useAuthStore()
const adminStore = useSystemAdminUserStore()
const toast = useToast()

const isSaving = ref(false)
const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const close = () => {
  emit('update:visible', false)
  passwordForm.currentPassword = ''
  passwordForm.newPassword = ''
  passwordForm.confirmPassword = ''
}

const handleSave = async () => {
  if (!props.isAdminMode && !passwordForm.currentPassword) {
    toast.add({ severity: 'error', summary: 'Erro', detail: 'A senha atual é obrigatória', life: 3000 })
    return
  }
  if (!passwordForm.newPassword) {
    toast.add({ severity: 'error', summary: 'Erro', detail: 'A nova senha é obrigatória', life: 3000 })
    return
  }
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    toast.add({ severity: 'error', summary: 'Erro', detail: 'As senhas não coincidem', life: 3000 })
    return
  }

  isSaving.value = true
  try {
    if (props.isAdminMode) {
      if (!props.userId) throw new Error('User ID is required in admin mode')
      await adminStore.adminChangePassword(props.userId, passwordForm.newPassword)
      toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Senha do usuário redefinida com sucesso', life: 3000 })
    } else {
      await authStore.changePassword(passwordForm.newPassword, passwordForm.currentPassword)
      toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Sua senha foi alterada com sucesso', life: 3000 })
    }
    emit('success')
    close()
  } catch (error: any) {
    console.error('Password update error:', error)
    const detail = error.response?.data?.detail || error.message || 'Falha ao atualizar senha'
    toast.add({ severity: 'error', summary: 'Erro', detail, life: 5000 })
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="emit('update:visible', $event)"
    :header="isAdminMode ? 'Redefinir Senha do Usuário' : 'Alterar Minha Senha'"
    modal
    class="w-full max-w-md"
    :draggable="false"
  >
    <div class="flex flex-col gap-4 mt-2">
      <p v-if="isAdminMode && userName" class="text-sm text-surface-600">
        Defina uma nova senha para <strong>{{ userName }}</strong>
      </p>

      <div v-if="!isAdminMode" class="flex flex-col gap-2">
        <label for="current_password" class="text-sm font-medium text-surface-700">Senha Atual</label>
        <Password
          id="current_password"
          v-model="passwordForm.currentPassword"
          class="w-full"
          toggleMask
          placeholder="Digite sua senha atual"
          autofocus
          :feedback="false"
          fluid
        />
      </div>
      
      <div class="flex flex-col gap-2">
        <label for="new_password" class="text-sm font-medium text-surface-700">Nova Senha</label>
        <Password
          id="new_password"
          v-model="passwordForm.newPassword"
          class="w-full"
          toggleMask
          placeholder="Digite a nova senha"
          :autofocus="isAdminMode"
          :feedback="false"
          fluid
        />
      </div>
      
      <div class="flex flex-col gap-2">
        <label for="confirm_password" class="text-sm font-medium text-surface-700">Confirmar Nova Senha</label>
        <Password
          id="confirm_password"
          v-model="passwordForm.confirmPassword"
          class="w-full"
          toggleMask
          placeholder="Confirme a nova senha"
          :feedback="false"
          fluid
        />
      </div>
    </div>
    
    <template #footer>
      <BaseButton
        text
        label="Cancelar"
        @click="close"
        :disabled="isSaving"
      />
      <BaseButton
        :label="isAdminMode ? 'Redefinir Senha' : 'Alterar Senha'"
        @click="handleSave"
        :loading="isSaving"
      />
    </template>
  </Dialog>
</template>
